/** Tela de entrada.
 *
 * A composição segue o conceito do produto: a régua de silêncio aparece aqui,
 * na coluna de apresentação, porque é a primeira coisa que alguém precisa
 * entender sobre o Tanzuh — que ele mede o tempo desde a última conversa.
 * Explicar isso na entrada custa uma linha e poupa uma tela de onboarding.
 */

import { useState, type SyntheticEvent } from "react";

import { ApiError, FalhaDeRede } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";
import { PortalDeAcesso } from "@/shared/ui/PortalDeAcesso";
import { ReguaDeSilencio } from "@/shared/ui/ReguaDeSilencio";

import { useSessao } from "./SessaoProvider";

export function TelaDeEntrada({
  aoPedirCadastro,
  aoEsquecerSenha,
}: {
  aoPedirCadastro?: () => void;
  aoEsquecerSenha?: () => void;
}) {
  const { entrar } = useSessao();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [bloqueada, setBloqueada] = useState(false);

  async function enviar(evento: SyntheticEvent) {
    evento.preventDefault();
    setErro(null);
    setBloqueada(false);
    setEnviando(true);

    try {
      await entrar(email, senha);
    } catch (falha) {
      if (falha instanceof ApiError) {
        // O bloqueio por tentativas é a única falha de login que o servidor
        // distingue — e com razão: a pessoa precisa saber que deve esperar,
        // não tentar de novo achando que errou a senha.
        setBloqueada(falha.status === 409);
        setErro(falha.message);
      } else if (falha instanceof FalhaDeRede) {
        setErro("Não conseguimos falar com o servidor. Verifique sua conexão.");
      } else {
        setErro("Algo deu errado. Tente de novo.");
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PortalDeAcesso painel={<Apresentacao />}>
      <form onSubmit={enviar} noValidate className="flex w-full flex-col gap-5">
        <header className="flex flex-col gap-1">
          <h1 className="font-display text-display-md text-texto">Entrar</h1>
          <p className="font-texto text-body text-texto-suave">
            Suas conversas e oportunidades, do jeito que você deixou.
          </p>
        </header>

        {erro !== null && (
          <div
            role="alert"
            className={[
              "rounded-md border px-4 py-3 font-texto text-body",
              bloqueada
                ? "border-ambar/40 bg-ambar/10 text-texto"
                : "border-carmim/40 bg-carmim/10 text-texto",
            ].join(" ")}
          >
            {erro}
          </div>
        )}

        <Campo
          rotulo="E-mail"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          placeholder="voce@empresa.com.br"
        />

        <Campo
          rotulo="Senha"
          type="password"
          name="senha"
          autoComplete="current-password"
          required
          value={senha}
          onChange={(e) => {
            setSenha(e.target.value);
          }}
        />

        <Botao type="submit" carregando={enviando} larguraTotal tamanho="lg">
          Entrar
        </Botao>

        {aoEsquecerSenha !== undefined && (
          <p className="text-center font-texto text-caption">
            <button
              type="button"
              onClick={aoEsquecerSenha}
              className="rounded-sm text-texto-suave underline underline-offset-2 hover:text-texto focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao-viva"
            >
              Esqueci minha senha
            </button>
          </p>
        )}

        {aoPedirCadastro !== undefined && (
          <p className="text-center font-texto text-caption text-texto-suave">
            Ainda não tem conta?{" "}
            <button
              type="button"
              onClick={aoPedirCadastro}
              className="rounded-sm font-medium text-acao underline underline-offset-2 hover:text-acao-viva focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acao-viva"
            >
              Criar a conta da sua empresa
            </button>
          </p>
        )}
      </form>
    </PortalDeAcesso>
  );
}

/** O painel de quem já conhece o produto e está voltando.
 *
 * Mostra o que o Tanzuh mede — a régua de silêncio —, porque é a única ideia
 * que precisa estar clara antes de usar o board, e explicá-la aqui custa uma
 * linha e poupa uma tela de onboarding. A moldura de duas colunas vive em
 * `PortalDeAcesso`; daqui para baixo é só a mensagem desta porta.
 */
function Apresentacao() {
  return (
    <div className="flex max-w-[440px] flex-col gap-6">
      <p className="font-display text-display-lg leading-tight text-acao-contraste">
        O CRM onde você trabalha pelo WhatsApp.
      </p>
      <p className="font-texto text-body-lg text-acao-contraste/85">
        A IA organiza, lembra e documenta. Você confirma.
      </p>

      <div className="mt-2 flex flex-col gap-3 rounded-lg bg-superficie p-5 shadow-e3">
        <ReguaDeSilencio
          diasComSinal={[
            true,
            true,
            true,
            false,
            true,
            true,
            false,
            false,
            false,
            false,
            false,
            false,
            false,
            false,
          ]}
        />
        <div className="flex flex-col gap-1">
          <p className="font-display text-title text-texto">Renovação Acme 2027</p>
          <p className="font-texto text-caption text-texto-suave">Acme Ltda · João Silva</p>
        </div>
        <div className="flex items-baseline gap-4 font-dados text-body text-texto">
          <span>R$ 85.000</span>
          <span className="text-texto-suave">50%</span>
          <span className="text-ambar">7 dias em silêncio</span>
        </div>
      </div>

      <p className="font-texto text-caption text-acao-contraste/75">
        Aquela faixa no topo do card mostra os últimos 14 dias. Cada traço aceso é um dia com
        conversa — o trecho âmbar é o silêncio que já pede atenção.
      </p>
    </div>
  );
}
